<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1000px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Company"
      text="Edit Company"
      class="mb-0"
      :loading="loading"
    >
      <form 
        v-if="dialog && !loading"
        class="overflow-y-auto"
        data-simplebar
        style="max-height: 500px;"
      >
        <v-container>
          <v-row
            no-gutters
            class="mb-4"
          >
            <v-col cols="12">
              <setup-companies-logo-upload
                v-model="logo"
                :existing-url="localCompany.logo_url"
                :removed.sync="logoRemoved"
                :disabled="progressing"
                submit-label="Update"
              />
            </v-col>
          </v-row>

          <v-divider class="mb-4" />

          <v-row no-gutters>
            <v-col
              cols="8"
              class="mt-1 pr-2"
            >
              <v-text-field
                v-model="localCompany.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Company Name"
                outlined
                @input="nameOnChanged"
              />
            </v-col>

            <v-col 
              cols="4"
            >
              <v-checkbox
                v-model="localCompany.is_active"
                color="green"
                hide-details
                class="px-4"
              >
                <template v-slot:label>
                  <div>
                    Active Company?
                    <v-tooltip 
                      slot="append"
                      top
                    >
                      <template v-slot:activator="{ on }">
                        <v-icon
                          color="warning" 
                          class="pl-1"
                          v-on="on"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>Inactive company will NOT be listed for company filter under payroll module</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
            </v-col>
            
            <v-col 
              cols="8"
              class="pr-2"
            >
              <v-text-field
                v-model="localCompany.formerly_known"
                v-validate="isFormerlyKnownRequired ? 'required' : ''"
                :error-messages="errors.collect('formerly_known')"
                data-vv-name="formerly_known"
                data-vv-as="formerly known"
                label="Formerly Known"
                outlined
                @input="localCompany.formerly_known = localCompany.formerly_known.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-menu
                v-model="effectiveDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="effectiveDateDisplay"
                    v-validate="isEffectiveDateRequired ? 'required' : ''"
                    label="New Company Name Effective Date"
                    outlined
                    :error-messages="errors.collect('effectiveDateDisplay')"    
                    data-vv-name="effectiveDateDisplay"
                    readonly
                    data-vv-as="new company name effective date"
                    class="label-position"
                    v-on="on" 
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localCompany.effective_date"
                  @input="effectiveDateMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-autocomplete
                v-model="localCompany.organization_lookup_uid"
                v-validate="'required'"
                :items="companyGroups"
                :error-messages="errors.collect('organization_lookup_uid')"
                data-vv-name="organization_lookup_uid"
                data-vv-as="company group"
                item-text="description1"
                item-value="lookup_uid"
                label="Company Group"
                hide-selected
                outlined
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-text-field
                v-model="localCompany.registration_no"
                v-validate="`required|isExistingRegistrationNo:${localCompany.id}`"
                outlined
                :error-messages="errors.collect('registration_no')"
                data-vv-name="registration_no"                
                data-vv-as="registration no"                
                label="Registration No"
                @input="localCompany.registration_no = localCompany.registration_no.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-text-field
                v-model="localCompany.old_registration_no"
                outlined              
                label="Old Registration No"
                @input="localCompany.old_registration_no = localCompany.old_registration_no.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-text-field
                v-model="localCompany.employer_epf_number"
                v-validate="'required'"
                outlined
                :error-messages="errors.collect('employer_epf_number')"
                data-vv-name="employer_epf_number"                
                data-vv-as="employer EPF no"                
                label="Employer EPF No."
                @input="localCompany.employer_epf_number = localCompany.employer_epf_number.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-text-field
                v-model="localCompany.employer_socso_number"
                v-validate="'required'"
                outlined
                :error-messages="errors.collect('employer_socso_number')"
                data-vv-name="employer_socso_number"                
                data-vv-as="employer SOCSO no"                
                label="Employer SOCSO No."
                @input="localCompany.employer_socso_number = localCompany.employer_socso_number.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-text-field
                v-model="localCompany.employer_eis_number"
                v-validate="'required'"
                outlined
                :error-messages="errors.collect('employer_eis_number')"
                data-vv-name="employer_eis_number"                
                data-vv-as="employer EIS no"                
                label="Employer EIS No."
                @input="localCompany.employer_eis_number = localCompany.employer_eis_number.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-text-field
                v-model="localCompany.employer_income_tax_number"
                v-validate="'required|length:11|validAlphanumeric'"
                outlined
                :error-messages="errors.collect('employer_income_tax_number')"
                data-vv-name="employer_income_tax_number"                
                data-vv-as="employer income tax no"                
                label="Employer Income Tax No."
                @input="localCompany.employer_income_tax_number = localCompany.employer_income_tax_number.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-text-field  
                v-model="localCompany.phone_number"
                v-validate="'required|phoneLength'"
                v-mask="['###-########']"
                :error-messages="errors.collect('phone_number')"
                data-vv-name="phone_number"                
                data-vv-as="phone_number"    
                label="Company Phone No."
                outlined
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-text-field  
                v-model="localCompany.email"
                v-validate="'email'"
                :error-messages="errors.collect('email')"
                data-vv-name="email"                
                data-vv-as="email"    
                label="Company Email"
                outlined
              />
            </v-col>

            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-text-field
                v-model="localCompany.hrdf_rate"
                v-validate="'max_value:100'"
                :error-messages="errors.collect('hrdfRate')"
                data-vv-name="hrdfRate"
                data-vv-as="hrdf rate"
                label="HRDF Rate (%)"
                outlined
                type="number"
                :min="0"
                :max="100"
                @keydown.native="['e', 'E', '+', '-'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-text-field  
                v-model="localCompany.invoice_contact_number"
                v-validate="'required|phoneLength'"
                v-mask="['###-########']"
                :error-messages="errors.collect('invoice_contact_number')"
                data-vv-name="invoice_contact_number"                
                data-vv-as="invoice contact no"    
                label="Invoice Contact No."
                outlined
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-text-field  
                v-model="localCompany.invoice_email"
                v-validate="'required|email'"
                :error-messages="errors.collect('invoice_email')"
                data-vv-name="invoice_email"                
                data-vv-as="invoice email"    
                label="Invoice Email"
                outlined
              />
            </v-col>   

            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-text-field
                v-model="localCompany.tin_number"
                v-validate="'required'"
                :error-messages="errors.collect('tin_number')"
                data-vv-name="tin_number"
                data-vv-as="TIN no"
                label="TIN No."
                outlined
                @input="localCompany.tin_number = localCompany.tin_number.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-text-field
                v-model="localCompany.sst_registration_number"
                :error-messages="errors.collect('sst_registration_number')"
                data-vv-name="sst_registration_number"
                data-vv-as="SST registration no"
                label="SST Registration No."
                outlined
                @input="localCompany.sst_registration_number = localCompany.sst_registration_number.toUpperCase()"
              />
            </v-col>
          </v-row>

          <v-divider class="mb-1" />

          <span class="font-weight-bold font-calibri font-18 black--text">Address</span>
          <v-row
            no-gutters
            class="mt-2"
          >
            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-text-field
                v-model="localCompany.address_line_one"
                v-validate="'required'"
                :error-messages="errors.collect('address_line_one')"
                data-vv-name="address_line_one"
                data-vv-as="address line 1"
                label="Address Line 1"
                outlined
                @input="localCompany.address_line_one = localCompany.address_line_one.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-text-field
                v-model="localCompany.address_line_two"
                label="Address Line 2"
                :disabled="!localCompany.address_line_one"
                :filled="!localCompany.address_line_one"
                outlined
                @input="localCompany.address_line_two = localCompany.address_line_two.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-text-field
                v-model="localCompany.address_line_three"
                label="Address Line 3"
                :disabled="!localCompany.address_line_two"
                :filled="!localCompany.address_line_two"
                outlined
                @input="localCompany.address_line_three = localCompany.address_line_three.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-autocomplete
                v-model="localCompany.postcode"
                v-validate="'required'"
                :items="postcodes"
                item-text="postcode"
                item-value="postcode"
                value="postcode"
                :error-messages="errors.collect('postcode')"
                data-vv-name="postcode"
                data-vv-as="postcode"
                label="Postcode"
                outlined
                @input="clearData(); postCodeOnChanged(localCompany.postcode)"
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-autocomplete
                v-model="localCompany.city_id"
                v-validate="'required'"
                :disabled="dropdownDisabled"
                :filled="!localCompany.postcode"
                :items="filteredCities"
                :error-messages="errors.collect('city_id')"
                data-vv-name="city_id"
                data-vv-as="city"
                item-text="name"
                item-value="id"
                value="id"
                label="City"
                hide-selected
                outlined
                @input="cityOnChanged(localCompany.city_id)"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-autocomplete
                v-model="localCompany.state_id"
                v-validate="'required'"
                :error-messages="errors.collect('state_id')"
                data-vv-name="state_id"
                data-vv-as="state"
                disabled
                :filled="!localCompany.city_id"
                :items="states"
                item-text="name"
                item-value="id"
                value="id"
                label="State"
                hide-selected
                outlined
              />
            </v-col>          
          </v-row>

          <v-divider class="mb-1" />

          <span class="font-weight-bold font-calibri font-18 black--text">Person In Charge Info</span>
          <v-row
            no-gutters
            class="mt-2"
          >
            <v-col 
              cols="4"
              class="pr-2"
            >
              <v-text-field
                v-model="localCompany.person_in_charge"
                v-validate="'required'"
                outlined
                :error-messages="errors.collect('person_in_charge')"
                data-vv-name="person_in_charge"                
                data-vv-as="person in charge"               
                label="Person In Charge"
                @input="localCompany.person_in_charge = localCompany.person_in_charge.toUpperCase()"
              />
            </v-col>

            <v-col 
              cols="4"
              class="px-2"
            >
              <v-text-field  
                v-model="localCompany.person_in_charge_ic"
                v-validate="'required|icDate|icLength'"
                v-mask="'######-##-####'"
                :error-messages="errors.collect('person_in_charge_ic')"
                data-vv-name="person_in_charge_ic"                
                data-vv-as="person in charge ic"    
                label="Person In Charge IC"
                outlined
                @input="icValidation(localCompany.person_in_charge_ic)"
              />
            </v-col>

            <v-col 
              cols="4"
              class="pl-2"
            >
              <v-text-field
                v-model="localCompany.person_in_charge_position"
                v-validate="'required'"
                outlined
                :error-messages="errors.collect('person_in_charge_position')"
                data-vv-name="person_in_charge_position"                
                data-vv-as="person in charge position"                
                label="Person In Charge Position"
                @input="localCompany.person_in_charge_position = localCompany.person_in_charge_position.toUpperCase()"
              />
            </v-col>         
          </v-row>
        </v-container>
      </form>

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"  
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"      
          class="white--text px-10"        
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new',
    },
    components: {
      SetupCompaniesLogoUpload: () => import('@/components/hris/setup/companies/LogoUpload'),
    },
    props: {
      company: {
        type: Object,
        default: null,
      },
      companyGroups: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        dialog: null,
        progressing: false,
        localCompany: null,
        effectiveDateMenu: false, 
        loading: false,
        states: [],
        postcodes: [],
        cities: [],
        dropdownDisabled: null,
        cityIds: [],
        logo: null,
        logoRemoved: false,
      }
    },
    computed: {
      effectiveDateDisplay () {
        return this.localCompany.effective_date ? this.$moment(this.localCompany.effective_date, 'YYYY-MM-DD').format('DD-MM-YYYY') : ''
      },
      isNameChanged () {
        return !!this.localCompany && this.localCompany.name !== this.company.name
      },
      isFormerlyKnownRequired () {
        return !!this.localCompany && (!!this.localCompany.effective_date || this.isNameChanged)
      },
      isEffectiveDateRequired () {
        return !!this.localCompany && (!!this.localCompany.formerly_known || this.isNameChanged)
      },
      filteredCities () {
        if (this.localCompany.postcode) {
          this.postCodeOnChanged(this.localCompany.postcode)
        }

        if (this.cityIds.length > 0) {
          return this.cities.filter(city => {
            return this.cityIds.includes(city.id)
          })
        }
        return this.cities      
      },        
    },
 
    watch: {
      isFormerlyKnownRequired: function (value) {
        if (!value) this.errors.remove('formerly_known')
      },
      isEffectiveDateRequired: function (value) {
        if (!value) this.errors.remove('effectiveDateDisplay')
      },
      dialog: function (value) {
        if (!value) return

        let scope = this
        this.loading = true
        this.localCompany = { ...this.company }
        this.logo = null
        this.logoRemoved = false

        this.localCompany.hrdf_rate = this.localCompany.hrdf_rate / 100

        this.$validator.extend('icDate', {
          getMessage (field, args) {
            return 'Invalid IC Date'        
          },
          validate (value, args) {
            return scope.isValidDate(value) 
          },
        })
        this.$validator.extend('icLength', {
          getMessage (field, args) {
            return 'IC not in right length'        
          },
          validate (value, args) {
            return value.toString().length === 12
          },
        })
        this.$validator.extend('validAlphanumeric', {
          getMessage (field, args) {
            return 'Employer income tax number should not contain any special character'     
          },
          validate (value, args) {
            const regex = /^[A-Z0-9]*$/
            return regex.test(value)
          },
        })           
        this.$validator.extend('phoneLength', {
          getMessage (field, args) {
            return 'Invalid phone number (at least 9 numbers and max 11)'
          },
          validate (value, args) {
            return value.toString().length >= 10 && value.toString().length <= 12
          },
        })
        this.$validator.extend('isExistingRegistrationNo', {
          getMessage (field, args) {
            return 'Existing registration number found'
          },
          validate (value, [currentCompanyId]) {
            return !scope.companies.some(company => {
              return company.registration_no === value && company.id !== currentCompanyId
            })
          },
        })

        Promise.all([
          this.$api.states().list(),
          this.$api.cities().list(),
          this.$api.postcodes().list(),
        ])
          .then(([states, cities, postcodes]) => {
            this.states = states.data
            this.cities = cities.data
            this.postcodes = _.orderBy(postcodes.data, ['postcode'], ['asc'])
      
            this.loading = false
          })
      },
    },
    methods: {
      nameOnChanged () {
        this.localCompany.name = this.localCompany.name.toUpperCase()
        this.localCompany.formerly_known = this.isNameChanged ? this.company.name : this.company.formerly_known
        this.localCompany.effective_date = this.isNameChanged ? null : this.company.effective_date
      },
      isValidDate (value) {
        var y = parseInt(value.substr(0, 2))
        var m = parseInt(value.substr(2, 2))
        var d = parseInt(value.substr(4, 2))
        if (m === 0 || m > 12) { return false }
        var monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ]
        // Adjust for leap years
        if (y % 400 === 0 || (y % 100 !== 0 && y % 4 === 0)) { monthLength[1] = 29 }
        return d > 0 && d <= monthLength[m - 1]
      },         
      icValidation (value) {
        if (value.toString().length === 14) {
          this.localCompany.person_in_charge_ic = value.replace(/-/g, '')
          this.$validator.validate('person_in_charge_ic', this.localCompany.person_in_charge_ic)
        }
      },        
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true
        
            this.$api.hrisCompanies().update(this.company.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.dialog = false
                }
              })
              .catch((err) => {
                if (err.response.status === 500) {
                  this.$snack({
                    color: 'error',
                    message: err.response.data.status || err.response.data.message || 'Failed to update company',
                  })
                } else if (err.response.data.status || err.response.data.message) {
                  this.$snack({
                    color: 'error',
                    message: err.response.data.status || err.response.data.message,
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
   
      prepareFormData () {
        let formData = this.$toFormData({
          name: this.localCompany.name,
          old_registration_no: this.localCompany.old_registration_no,
          registration_no: this.localCompany.registration_no, 
          formerly_known: this.localCompany.formerly_known, 
          effective_date: this.localCompany.effective_date, 
          employer_epf_number: this.localCompany.employer_epf_number, 
          employer_eis_number: this.localCompany.employer_eis_number,
          employer_socso_number: this.localCompany.employer_socso_number,
          employer_income_tax_number: this.localCompany.employer_income_tax_number,
          person_in_charge: this.localCompany.person_in_charge,
          person_in_charge_ic: this.localCompany.person_in_charge_ic,
          person_in_charge_position: this.localCompany.person_in_charge_position,
          phone_number: this.localCompany.phone_number,
          hrdf_rate: this.localCompany.hrdf_rate * 100,
          address_line_one: this.localCompany.address_line_one,
          address_line_two: this.localCompany.address_line_two,
          address_line_three: this.localCompany.address_line_three,
          postcode: this.localCompany.postcode,
          city_id: this.localCompany.city_id,
          state_id: this.localCompany.state_id,
          email: this.localCompany.email,
          tin_number: this.localCompany.tin_number,
          sst_registration_number: this.localCompany.sst_registration_number,
          invoice_contact_number: this.localCompany.invoice_contact_number,
          invoice_email: this.localCompany.invoice_email,
          is_active: this.localCompany.is_active,
          organization_lookup_uid: this.localCompany.organization_lookup_uid,
        })

        if (this.logo) {
          formData.append('logo', this.logo.imageFile)
        } else if (this.logoRemoved) {
          formData.append('remove_logo', 1)
        }

        return formData
      },
      clearData () {
        this.localCompany.city_id = null
        this.localCompany.state_id = null
      },
      postCodeOnChanged (postcodeValue) {
        var cityID = this.postcodes.filter(p => {
          return p.postcode === postcodeValue
        })  

        this.cityIds = this.cities.filter(city => {
          return cityID.some(city2 => {
            return city2.city_id === city.id
          })
        }).map(city3 => {
          return city3.id
        })

        if (this.cityIds.length > 1) {
          this.dropdownDisabled = false
        } else {
          this.dropdownDisabled = true

          let cityId = (this.postcodes.find(postcode => {
            return postcode.postcode === postcodeValue
          }) || {}).city_id

          if (cityId) {
            var cityObject = this.getCity(cityId)
            if (cityObject) {
              this.localCompany.city_id = cityObject.id
              var stateObject = this.getState(cityObject.state_id)
              
              if (stateObject) {
                this.localCompany.state_id = stateObject.id
              }
            }
          }
        }
      },
      cityOnChanged (cityId) {
        var cityObject = this.getCity(cityId)
        if (cityObject) {
          var stateObject = this.getState(cityObject.state_id)
          
          if (stateObject) {
            this.localCompany.state_id = stateObject.id
          }
        }
      },      
      getCity (cityId) {
        if (isNaN(parseInt(cityId))) return null
        return this.cities.find(city => {
          return city.id === parseInt(cityId)
        })
      },
      getState (stateId) {
        if (isNaN(parseInt(stateId))) return null
        return this.states.find(state => {
          return state.id === parseInt(stateId)
        })
      },    
    },
  }
</script>
<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:1px
}
.font-18{
  font-size: 18px;
}
.font-calibri{
  font-family: Calibri;
}
</style>
